// avisoProfesorCambio.js — «Prepáralo»: aviso al profesor cuando un alumno suyo reserva, cambia o cancela una práctica
// (14/10/2026, Llano 11e). Ficha: docs/features/autoescuela-aviso-profesor-cambio-alumno.md
//
// El cuerpo entre las marcas es COPIA POR VALOR de aws_lambdas/_shared_autoescuela/avisoProfesor.js (la tarjeta valida
// y la vista previa escribe EXACTAMENTE lo que mandará el servidor). Se edita allí y se copia con
// `node aws_lambdas/_shared_autoescuela/avisoProfesorCopias.js`; avisoProfesorCopias.test.js vigila.
/* eslint-disable */
// ── avisoProfesor: cuerpo compartido (inicio) ──
const AVISO_PROFESOR_DEFECTO = Object.freeze({
  enabled: false, desde: "08:00", hasta: "21:30", tipos: Object.freeze({ reserva: true, cambio: true, cancelacion: true }),
});
// La ventana va en medias horas entre las 07:00 y las 22:30 (lo que cubre el cron de media hora, que vacía a `desde`
// lo que se quedó esperando por la noche).
const AVISO_PROFESOR_MIN = "07:00";
const AVISO_PROFESOR_MAX = "22:30";
const AVISO_TIPO_DE_EVENTO = Object.freeze({ practica_reservada: "reserva", practica_movida: "cambio", practica_cancelada: "cancelacion" });
const AVISO_MAX_LINEAS = 3;

const esObjetoAP = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const esMediaHora = (s) => typeof s === "string" && /^([01]\d|2[0-3]):(00|30)$/.test(s) && s >= AVISO_PROFESOR_MIN && s <= AVISO_PROFESOR_MAX;

/** Siempre completa (nunca lanza). */
function normalizaAvisoProfesor(raw) {
  const r = esObjetoAP(raw) ? raw : {};
  const t = esObjetoAP(r.tipos) ? r.tipos : {};
  let desde = esMediaHora(r.desde) ? r.desde : AVISO_PROFESOR_DEFECTO.desde;
  let hasta = esMediaHora(r.hasta) ? r.hasta : AVISO_PROFESOR_DEFECTO.hasta;
  if (desde >= hasta) { desde = AVISO_PROFESOR_DEFECTO.desde; hasta = AVISO_PROFESOR_DEFECTO.hasta; }
  return {
    enabled: r.enabled === true,
    desde,
    hasta,
    tipos: { reserva: t.reserva !== false, cambio: t.cambio !== false, cancelacion: t.cancelacion !== false },
  };
}

/** Lo que llega del navegador → { value } | { error } (mensaje para enseñar tal cual). */
function validaAvisoProfesor(raw) {
  if (!esObjetoAP(raw)) return { error: "aviso_profesor no tiene el formato esperado." };
  if (raw.enabled !== undefined && typeof raw.enabled !== "boolean") return { error: "aviso_profesor.enabled debe ser true o false." };
  for (const k of ["desde", "hasta"]) {
    if (raw[k] !== undefined && !esMediaHora(raw[k])) return { error: `La hora tiene que ir en punto o y media, de ${AVISO_PROFESOR_MIN} a ${AVISO_PROFESOR_MAX}.` };
  }
  const desde = raw.desde || AVISO_PROFESOR_DEFECTO.desde;
  const hasta = raw.hasta || AVISO_PROFESOR_DEFECTO.hasta;
  if (desde >= hasta) return { error: "La hora de empezar a avisar tiene que ser anterior a la de dejar de avisar." };
  if (raw.tipos !== undefined) {
    if (!esObjetoAP(raw.tipos)) return { error: "aviso_profesor.tipos no tiene el formato esperado." };
    for (const k of Object.keys(raw.tipos)) {
      if (!["reserva", "cambio", "cancelacion"].includes(k) || typeof raw.tipos[k] !== "boolean") return { error: `aviso_profesor.tipos.${k} no vale.` };
    }
  }
  return { value: normalizaAvisoProfesor(raw) };
}

/** ¿Es hora de avisar? `hhmm` = hora de Madrid «HH:MM». */
function dentroDeVentana(cfg, hhmm) {
  const c = normalizaAvisoProfesor(cfg);
  return typeof hhmm === "string" && hhmm >= c.desde && hhmm < c.hasta;
}

const DIAS_AP = { es: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"], ca: ["diumenge", "dilluns", "dimarts", "dimecres", "dijous", "divendres", "dissabte"] };

/** { dia: «martes 13/10», hora: «10:00», ymd } de un ISO, en hora de Madrid. */
function cuandoAP(iso, lang) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return null;
  const p = {};
  for (const x of new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Madrid", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(ms))) p[x.type] = x.value;
  const ymd = `${p.year}-${p.month}-${p.day}`;
  const dow = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day))).getUTCDay();
  return { dia: `${DIAS_AP[lang === "ca" ? "ca" : "es"][dow]} ${Number(p.day)}/${Number(p.month)}`, hora: `${p.hour}:${p.minute}`, ymd };
}

/**
 * Una línea del aviso. linea = { tipo: "reserva"|"cambio"|"cancelacion", start, antes? }. '' si falta algo.
 *   es: «reserva una el jueves 15/10 a las 12:00» · «pasa la del martes 13/10 a las 10:00 al jueves 15/10 a las 12:00»
 *       · «pasa la del martes 13/10 de las 10:00 a las 12:00» · «cancela la del martes 13/10 a las 10:00»
 *   ca: «en reserva una dijous 15/10 a les 12:00» · «passa la de dimarts 13/10 a les 10:00 a dijous 15/10 a les 12:00»
 *       · «passa la de dimarts 13/10 de les 10:00 a les 12:00» · «cancel·la la de dimarts 13/10 a les 10:00»
 */
function textoLineaAP(linea, lang) {
  const ca = lang === "ca";
  const b = cuandoAP(linea && linea.start, lang);
  if (!b) return "";
  if (linea.tipo === "reserva") return ca ? `en reserva una ${b.dia} a les ${b.hora}` : `reserva una el ${b.dia} a las ${b.hora}`;
  if (linea.tipo === "cancelacion") return ca ? `cancel·la la de ${b.dia} a les ${b.hora}` : `cancela la del ${b.dia} a las ${b.hora}`;
  if (linea.tipo === "cambio") {
    const a = cuandoAP(linea.antes, lang);
    if (!a) return "";
    if (a.ymd === b.ymd) return ca ? `passa la de ${a.dia} de les ${a.hora} a les ${b.hora}` : `pasa la del ${a.dia} de las ${a.hora} a las ${b.hora}`;
    return ca ? `passa la de ${a.dia} a les ${a.hora} a ${b.dia} a les ${b.hora}` : `pasa la del ${a.dia} a las ${a.hora} al ${b.dia} a las ${b.hora}`;
  }
  return "";
}

/** {{4}} de la plantilla: las líneas unidas en UNA frase sin saltos («a; b y c», «… y 2 cambios más»). */
function textoAvisoProfesor(lineas, lang) {
  const ca = lang === "ca";
  const txt = (lineas || []).map((l) => textoLineaAP(l, lang)).filter(Boolean);
  if (!txt.length) return "";
  const y = ca ? " i " : " y ";
  if (txt.length > AVISO_MAX_LINEAS) {
    const resto = txt.length - AVISO_MAX_LINEAS;
    const mas = ca ? `${resto} ${resto === 1 ? "canvi" : "canvis"} més` : `${resto} ${resto === 1 ? "cambio" : "cambios"} más`;
    return `${txt.slice(0, AVISO_MAX_LINEAS).join("; ")}${y}${mas}`;
  }
  if (txt.length === 1) return txt[0];
  return `${txt.slice(0, -1).join("; ")}${y}${txt[txt.length - 1]}`;
}

/**
 * Lo que de verdad ha cambiado tras la ráfaga, por práctica (en el orden en que llegaron). PURA.
 * eventos: [{ tipo: "practica_reservada"|"practica_movida"|"practica_cancelada", practica_id, start, antes? }]
 * actuales: { [practica_id]: práctica tal y como está AHORA (o null si ya no existe) }
 * → [{ tipo, start, antes?, practica_id }]: reservada y luego cancelada = nada; movida dos veces = de la primera hora a
 *   la última; movida y vuelta a su hora = nada; lo que ya no es de este profesor (salvo cancelar) = nada; lo que ya
 *   pasó (`nowMs`) = nada; y los tipos que la autoescuela no quiere avisar, tampoco.
 */
function lineasNetas({ eventos, actuales, instructor_id, nowMs, tipos }) {
  const orden = [];
  const porPractica = {};
  for (const e of eventos || []) {
    if (!e || !e.practica_id || !AVISO_TIPO_DE_EVENTO[e.tipo]) continue;
    if (!porPractica[e.practica_id]) { porPractica[e.practica_id] = []; orden.push(e.practica_id); }
    porPractica[e.practica_id].push(e);
  }
  const quiere = normalizaAvisoProfesor({ tipos }).tipos;
  const out = [];
  for (const id of orden) {
    const evs = porPractica[id];
    const p = actuales ? actuales[id] : null;
    if (!p) continue;
    const reservadaAhora = evs.some((e) => e.tipo === "practica_reservada");
    const primeraMovida = evs.find((e) => e.tipo === "practica_movida" && e.antes);
    const cancelada = p.status === "cancelada";
    let linea = null;
    if (cancelada) {
      if (reservadaAhora) continue; // la reservó y la canceló en la misma ráfaga: no hay nada que preparar
      const start = (primeraMovida && primeraMovida.antes) || p.start;
      linea = { tipo: "cancelacion", start, practica_id: id };
    } else {
      if (instructor_id && String(p.instructor_id || "") !== String(instructor_id)) continue;
      if (reservadaAhora) linea = { tipo: "reserva", start: p.start, practica_id: id };
      else if (primeraMovida) {
        if (primeraMovida.antes === p.start) continue; // la movió y la devolvió a su hora
        linea = { tipo: "cambio", antes: primeraMovida.antes, start: p.start, practica_id: id };
      }
    }
    if (!linea || !quiere[linea.tipo]) continue;
    const ms = Date.parse(linea.start || "");
    if (Number.isFinite(nowMs) && Number.isFinite(ms) && ms < nowMs) continue;
    out.push(linea);
  }
  return out;
}
// ── avisoProfesor: cuerpo compartido (fin) ──

export {
  AVISO_PROFESOR_DEFECTO, AVISO_TIPO_DE_EVENTO, AVISO_MAX_LINEAS,
  normalizaAvisoProfesor, validaAvisoProfesor, dentroDeVentana, cuandoAP, textoLineaAP, textoAvisoProfesor, lineasNetas,
};
